/**
 * ErrorsTab (Admin › Activity › Errors, ADR-052): everything that failed,
 * newest first, grouped by what failed and where. Each row says who,
 * when, which screen, what happened in plain words and the reference the
 * person was told to quote. Opening a row shows both stories: the exact
 * words the person saw and what actually happened underneath, plus how
 * many times the same thing has happened. "Copy for Fable" puts all of
 * it on the clipboard.
 */
import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/primitives/Button';
import { useSupportContact } from '@/hooks/useSupportContact';
import { formatRelative } from '@/lib/format';
import {
  ERROR_WINDOWS,
  copyForFable,
  fetchErrorsReport,
  screenFor,
  theySaw,
  whatHappened,
  type ErrorGroupWire,
  type ErrorWindowDays,
  type ErrorsReportWire,
} from '@/lib/appErrors';
import { ErrorBanner } from './ErrorBanner';
import { SkeletonRow } from './Skeleton';

export interface ErrorsTabProps {
  tenantSlug: string;
}

function shortRef(reference: string): string {
  return reference.slice(0, 8);
}

function occurrences(g: ErrorGroupWire): string {
  const times = g.count === 1 ? 'once' : `${g.count} times`;
  const people = g.people === 1 ? '1 person' : `${g.people} people`;
  return g.count === 1 ? 'once' : `${people}, ${times}`;
}

export function ErrorsTab({ tenantSlug }: ErrorsTabProps): ReactNode {
  const [windowDays, setWindowDays] = useState<ErrorWindowDays>(7);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const contact = useSupportContact();

  const query = useQuery<ErrorsReportWire, Error>({
    queryKey: ['admin-review', 'errors', tenantSlug, windowDays],
    queryFn: ({ signal }) => fetchErrorsReport(tenantSlug, windowDays, signal),
    staleTime: 30_000,
  });

  const groups = query.data?.groups ?? [];
  const selected = groups.find((g) => g.key === selectedKey) ?? null;

  async function copySelected(): Promise<void> {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(copyForFable(selected));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div data-testid="errors-tab" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-ink">Errors</h2>
        <div role="group" aria-label="Time window" className="flex gap-1.5">
          {ERROR_WINDOWS.map((w) => (
            <Button
              key={w.days}
              variant={w.days === windowDays ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={w.days === windowDays}
              onClick={() => {
                setWindowDays(w.days);
                setSelectedKey(null);
              }}
            >
              {w.label}
            </Button>
          ))}
        </div>
      </div>

      {query.isError && <ErrorBanner error={query.error} onRetry={() => query.refetch()} what="the errors" />}

      {query.isLoading ? (
        <div role="status" aria-label="Loading">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      ) : groups.length === 0 ? (
        <p className="text-sm text-ink-soft" data-testid="errors-empty">
          Nothing failed in this window.
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
          <ul className="m-0 list-none divide-y divide-sage-100 rounded-[12px] border border-sage-400/20 p-0" data-testid="errors-list">
            {groups.map((g) => (
              <li key={g.key}>
                <Button
                  variant="ghost"
                  onClick={() => setSelectedKey(g.key)}
                  aria-pressed={g.key === selectedKey}
                  data-testid="errors-row"
                  className={
                    'block w-full !justify-start !rounded-none px-3 py-2.5 text-left text-sm font-normal ' +
                    (g.key === selectedKey ? 'bg-sage-50' : '')
                  }
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-ink">
                      {screenFor(g.route)}: {whatHappened(g)}
                    </span>
                    <span className="font-mono text-xs text-ink-soft whitespace-nowrap">ref {shortRef(g.latest.reference)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-ink-soft">
                    {formatRelative(g.latest.occurred_at)}
                    {g.latest.user_display_name ? ` · ${g.latest.user_display_name}` : ''}
                    {` · ${occurrences(g)}`}
                  </div>
                </Button>
              </li>
            ))}
          </ul>

          {selected && (
            <section
              className="rounded-[12px] border border-sage-400/20 bg-paper p-4 text-sm"
              data-testid="errors-detail"
              aria-label="Error detail"
            >
              <p className="text-xs text-ink-soft">
                ref {shortRef(selected.latest.reference)} · {formatRelative(selected.latest.occurred_at)}
              </p>
              <h3 className="mt-1 text-title-sm font-semibold text-ink">
                {screenFor(selected.route)}: {whatHappened(selected)}
              </h3>

              <p className="mt-3 text-xs text-ink-soft">They saw</p>
              <p className="mt-0.5 text-ink" data-testid="errors-they-saw">
                {theySaw(selected, contact)}
              </p>

              <p className="mt-3 text-xs text-ink-soft">What actually happened</p>
              <p className="mt-0.5 break-words font-mono text-xs text-ink" data-testid="errors-underneath">
                {selected.error_code}
                {selected.status !== null ? ` · HTTP ${selected.status}` : ''}
                {selected.latest.method ? ` · ${selected.latest.method}` : ''}
                {selected.latest.detail ? ` · ${selected.latest.detail}` : ''}
              </p>

              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                <dt className="text-ink-soft">Who</dt>
                <dd className="text-ink">{selected.latest.user_display_name ?? 'Unknown'}</dd>
                <dt className="text-ink-soft">Where</dt>
                <dd className="break-all font-mono text-xs text-ink">{selected.route}</dd>
                <dt className="text-ink-soft">Reference</dt>
                <dd className="break-all font-mono text-xs text-ink">{selected.latest.reference}</dd>
                <dt className="text-ink-soft">Same error</dt>
                <dd className="text-ink">
                  {selected.count === 1 ? 'Once' : `${selected.count} times`}
                  {selected.count > 1 ? ` since ${formatRelative(selected.first_at)}` : ''}
                </dd>
              </dl>

              <div className="mt-4">
                <Button variant="secondary" size="sm" onClick={() => void copySelected()} data-testid="errors-copy">
                  {copied ? 'Copied' : 'Copy for Fable'}
                </Button>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
